import type { IncidentCategory, UpdateIncidentCategoryRequest } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../../components/generic/Modal';
import { useUpdateIncidentCategoryMutation } from '../../../graphql/hooks/useUpdateIncidentCategoryMutation';
import { useAppContext } from '../../../hooks/useAppContext';
import { zodResolver } from '../../../utils/zod';
import { IncidentCategoryForm } from './IncidentCategoryForm';

type FormValues = UpdateIncidentCategoryRequest;

const schema: z.ZodSchema<FormValues> = z.object({
  name: z.string(),
  color: z.string(),
});

interface Props {
  category: IncidentCategory;
}

export const IncidentCategoryUpdateModal = createModal<Props>((props) => {
  const { category, modal } = props;
  const { hide, remove, visible } = modal;
  const { errorToast, successToast, organization } = useAppContext();

  const initialValues: FormValues = {
    name: category.name,
    color: category.color,
  };

  const methods = useForm<FormValues>({
    defaultValues: initialValues,
    mode: 'all',
    resolver: zodResolver(schema),
  });

  const mutation = useUpdateIncidentCategoryMutation();

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      await mutation
        .mutateAsync({
          organizationId: organization.id,
          categoryId: category.id,
          request: values,
        })
        .then(() => {
          hide();
          successToast('Incident category updated successfully');
        })
        .catch((err) => {
          errorToast(`Error updating incident category: ${err.message}`);
        });
    },
    [mutation, organization.id, category.id, hide, successToast, errorToast],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Edit an incident category'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>
                <IncidentCategoryForm />
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Save</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
